const digitsOnly = (value) => String(value || '').replace(/\D/g, '');

const phoneValidation = (value) => (
  /^\d{10,15}$/.test(String(value || ''))
    ? null
    : 'Debe tener entre 10 y 15 dígitos'
);

const optionalPhoneValidation = (value) => (
  !value || /^\d{10,15}$/.test(String(value))
    ? null
    : 'Debe tener entre 10 y 15 dígitos'
);

const documentValidation = (value) => (
  /^\d{7,11}$/.test(String(value || ''))
    ? null
    : 'Debe tener entre 7 y 11 dígitos'
);

const coordinatesValidation = (value) => (
  value?.lat !== null
  && value?.lat !== undefined
  && value?.lat !== ''
  && value?.lng !== null
  && value?.lng !== undefined
  && value?.lng !== ''
    ? null
    : 'Ingresa coordenadas válidas (lat, lng)'
);

export const buildTaskModalSections = ({
  taskTypes,
  teams,
  loadingTaskTypes,
  normalizeCoordinates,
  showFixedTeamForTour = false
}) => {
  const isRepairTaskType = (taskTypeName) => (
    Boolean(taskTypeName)
    && taskTypes.find((type) => type.name === taskTypeName)?.requiresTicketNumber
  );

  const taskTypeOptions = taskTypes.map((taskType) => ({
    value: taskType.name,
    label: taskType.displayName
  }));

  const teamOptions = [
    { value: '', label: 'Sin equipo (se asignará al iniciar jornada)' },
    ...teams.map((team) => ({ value: team.id, label: team.name }))
  ];

  return [
    {
      id: 'client',
      title: 'Datos del cliente',
      tour: 'task-form-client',
      className: 'pb-8',
      gridClassName: 'grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3',
      fields: [
        {
          name: 'client.name',
          label: 'Nombre',
          type: 'text',
          placeholder: 'Nombre del cliente',
          required: true
        },
        {
          name: 'client.phone',
          label: 'Teléfono',
          type: 'tel',
          inputMode: 'tel',
          placeholder: 'Teléfono del cliente',
          required: true,
          transformValue: digitsOnly,
          validate: phoneValidation
        },
        {
          name: 'client.secondaryPhone',
          label: 'Teléfono secundario',
          type: 'tel',
          inputMode: 'tel',
          placeholder: 'Teléfono secundario',
          transformValue: digitsOnly,
          validate: optionalPhoneValidation
        },
        {
          name: 'client.cuil',
          label: 'DNI / CUIL / CUIT',
          type: 'text',
          inputMode: 'numeric',
          maxLength: 11,
          placeholder: 'DNI/CUIL/CUIT del cliente',
          required: true,
          transformValue: digitsOnly,
          validate: documentValidation
        },
        {
          name: 'client.address',
          label: 'Dirección',
          type: 'text',
          placeholder: 'Dirección del cliente',
          required: true
        },
        {
          name: 'client.coordinates',
          label: 'Coordenadas',
          type: 'coordinates',
          tour: 'task-form-location',
          normalizeCoordinates,
          validate: coordinatesValidation
        }
      ]
    },
    {
      id: 'task',
      tour: 'task-form-title-date',
      className: 'border-t border-line pt-8',
      gridClassName: 'grid grid-cols-1 gap-4 sm:grid-cols-2',
      fields: [
        {
          name: 'title',
          label: 'Título de la tarea',
          type: 'text',
          placeholder: 'Ej: Instalación de fibra óptica - Cliente ABC',
          required: true
        },
        {
          name: 'scheduledDate',
          label: 'Fecha y hora programada',
          type: 'datetime',
          required: true
        },
        {
          name: 'priority',
          label: 'Prioridad',
          type: 'select',
          placeholder: 'Selecciona la prioridad',
          required: true,
          options: [
            { value: 'baja', label: 'Prioridad baja' },
            { value: 'media', label: 'Prioridad media' },
            { value: 'alta', label: 'Prioridad alta' }
          ]
        },
        {
          name: 'taskType',
          label: 'Tipo de tarea',
          type: 'select',
          tour: 'task-form-type',
          placeholder: 'Selecciona el tipo de tarea',
          required: true,
          disabled: loadingTaskTypes,
          options: taskTypeOptions,
          onValueChange: (value, values) => (
            isRepairTaskType(value) ? values : { ...values, ticketNumber: '' }
          )
        },
        {
          name: 'ticketNumber',
          label: 'Número de ticket',
          type: 'text',
          inputMode: 'numeric',
          placeholder: 'Número de ticket',
          required: true,
          transformValue: digitsOnly,
          hidden: (values) => !isRepairTaskType(values.taskType)
        },
        {
          name: 'teamId',
          label: 'Equipo',
          type: 'select',
          tour: 'task-form-team',
          required: true,
          options: teamOptions,
          className: (values) => (isRepairTaskType(values.taskType) ? '' : 'sm:col-span-2'),
          onValueChange: (value, values) => (
            value ? values : { ...values, fixedTeam: false }
          )
        },
        {
          name: 'description',
          label: 'Descripción',
          type: 'textarea',
          tour: 'task-form-description',
          placeholder: 'Detalles específicos de la tarea',
          rows: 3,
          className: 'sm:col-span-2'
        }
      ]
    },
    {
      id: 'options',
      tour: 'task-form-options',
      className: 'mt-8 border-t border-line pt-6',
      gridClassName: 'grid grid-cols-1 gap-4 sm:grid-cols-2',
      fields: [
        {
          name: 'isCorporate',
          label: '¿Es una tarea de alta complejidad?',
          type: 'toggle',
          onValueChange: (value, values) => (
            value
              ? values
              : { ...values, estimatedDuration: '', isTimeInHours: true }
          )
        },
        {
          name: 'fixedTeam',
          label: 'Fijar al equipo',
          type: 'toggle',
          reserveSpaceWhenHidden: true,
          hidden: (values) => !showFixedTeamForTour && (!values.teamId || values.teamId === 'null')
        },
        {
          name: 'estimatedDuration',
          label: 'Duración estimada',
          type: 'number',
          min: 1,
          placeholder: 'Ej: 5',
          hidden: (values) => !values.isCorporate,
          className: 'mt-2'
        },
        {
          name: 'isTimeInHours',
          label: 'Unidad de tiempo',
          type: 'select',
          hidden: (values) => !values.isCorporate,
          className: 'mt-2',
          transformValue: (value) => value === 'true',
          options: [
            { value: true, label: 'Horas' },
            { value: false, label: 'Días' }
          ]
        }
      ]
    }
  ];
};
